import * as React from 'react';
import { toast } from 'sonner';
import { copyToClipboard } from '../../../../models/UiUtils';
import { useClientTrackSatisfactionResponseClickThroughMutation } from '../../../../queries';
import { ISatisfactionSurvey, ISatisfactionSurveyResponse, ISurveyResponse } from '../../../models/Api';

export const useHandleSatisfactionSurveyResponse = ({
	response,
	survey,
	token,
}: {
	response?: ISatisfactionSurveyResponse;
	survey?: ISatisfactionSurvey;
	token?: string;
}) => {
	const reviewLinkAnchorRef = React.useRef<HTMLAnchorElement>(null);
	const responseRef = React.useRef<ISurveyResponse>(response);
	const { mutateAsync: trackSatisfactionSurveyResponseClickThroughAsync } =
		useClientTrackSatisfactionResponseClickThroughMutation({});

	const requestReview =
		survey?.reviewRequestSettings?.isEnabled && survey?.reviewRequestSettings.ratings?.indexOf(response?.rating) >= 0;

	const onCopyFeebackToClipboardClicked = React.useCallback(() => {
		const satisfactionSurveyResponse: ISatisfactionSurveyResponse = response;
		if (copyToClipboard(satisfactionSurveyResponse.feedback || '')) {
			toast.success("We've copied your review to your clipboard");
			return;
		}
		toast.error('Error copying feedback to clipboard.');
	}, [response]);

	const onReviewLinkClicked = React.useCallback(
		async (e: React.MouseEvent<HTMLElement>) => {
			const satisfactionSurveyResponse: ISatisfactionSurveyResponse = response;

			if (satisfactionSurveyResponse?.feedback?.length > 0) {
				onCopyFeebackToClipboardClicked();
			}
			if (responseRef.current && token) {
				e.preventDefault();
				await trackSatisfactionSurveyResponseClickThroughAsync({
					accessToken: token,
					response: responseRef.current as ISatisfactionSurveyResponse,
				});

				// clear the response ref and click the link again... thus, allowing the anchor to do what it should in the default case.
				// note: we use the reviewLinkAnchorRef to call back into the anchor... e.target could be stale
				responseRef.current = null;
				// delay to show the toast message
				setTimeout(() => {
					reviewLinkAnchorRef.current?.click();
				}, 400);
			}
		},
		[onCopyFeebackToClipboardClicked, response, token, trackSatisfactionSurveyResponseClickThroughAsync]
	);

	return {
		reviewLinkAnchorRef,
		trackSatisfactionSurveyResponseClickThroughAsync,
		requestReview,
		onCopyFeebackToClipboardClicked,
		onReviewLinkClicked,
	} as const;
};
